/* eslint max-classes-per-file: "off" */

import { isInternalURL, throttle } from '@js/src/shared-functions';
import {
  DATALAYER_NAME,
  BREAKPOINT_NAME_XS,
  BREAKPOINT_NAME_S,
  BREAKPOINT_NAME_M,
  BREAKPOINT_NAME_L,
  BREAKPOINT_NAME_XL,
  BREAKPOINT_NAME_XXL,
  getViewportWidth,
} from '@js/src/base-functions';
import { PollSelectors } from '@js/poll';

((BREAKPOINTS, globalDatalayer) => {
  const debugDatalayer = false;
  const VALUE_NOT_AVAILABLE = 'N/A'; // represents the default value, if there is no value expected

  class Utils {
    static log(items) {

      if (debugDatalayer) console.log(items);
    }

    static isUndefined(obj) {
      return typeof obj === 'undefined' || obj === null;
    }

    static isElement($element) {
      return ((typeof $element === 'object') && ($element instanceof HTMLElement));
    }

    static hasProp(obj, key) {
      return Object.prototype.hasOwnProperty.call(obj, key);
    }

    static replaceNullWithUndefined(obj) {
      let o = obj;

      Object.keys(o).forEach((key) => {
        if (o[key] === null) {
          o[key] = undefined;
        }

        if (typeof o[key] === 'object' && o[key] !== null) {
          this.replaceNullWithUndefined(o[key]);
        }
      });
    }

    static getCookies(str = document.cookie) {
      return str
        .split(';')
        .map((v) => v.split('='))
        .reduce((acc, v) => {
          if (v[0].trim()) {
            acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
          }

          return acc;
        }, {});
    }

    static getGaCookie() {
      const cookies = this.getCookies();

      if (cookies._ga) {
        return cookies._ga;
      }

      return '';
    }

    static validateLanguage(languageCode) {
    // ISO 639-1
      let lang = languageCode || null;
      const regex = /^([a-z]{2}|[a-z]{2}-[A-Z]{2})$/;

      if (lang === null || !regex.test(lang)) {
        return null;
      }

      return lang;
    }

    static getPageWordCount() {
      const countWords = (sectionElements) => {
        if (sectionElements.length === 0) return 0;

        let count = 0;
        for (let i = 0; i < sectionElements.length; i += 1) {
          count += sectionElements[i].innerText.split(' ').length;
        }

        return count;
      };

      const wordCount = countWords(document.querySelectorAll('section'));
      const words = (Math.round(wordCount * 100) / 100).toFixed(2);

      return parseFloat(words);
    }

    static getCurrentBreakpointName() {
      let breakpoint = '';
      // from base-functions.js
      const screenWidth = getViewportWidth();
      const screenSizes = BREAKPOINTS;

      // override size until it's not greater or equal than
      for (let i = 0; i < Object.keys(screenSizes).length; i += 1) {
        if (screenWidth >= Object.values(screenSizes)[i]) {
          breakpoint = Object.keys(screenSizes)[i];
        }
      }

      return breakpoint;
    }

    static isMobile() {
      return ['xs', 'sm', 'md'].includes(this.getCurrentBreakpointName());
    }

    /**
     * Determines whether the URL of a given link element is internal, external or undefined.
     * @param {HTMLAnchorElement} $linkElement - link element
     * @returns {string|undefined} tracking value used for "link type"
     */
    static getLinkType($linkElement) {
      let linkType;

      if (!this.isUndefined($linkElement)) {
        const linkHref = $linkElement.href || '';
        const isInternalLink = isInternalURL(linkHref);

        if (!this.isUndefined(isInternalLink)) {
          linkType = isInternalLink ? 'Internal' : 'External';
        }
      }

      return linkType;
    }

    /**
     * Returns trimmed text of element used as tracking value.
     * If there is no text for that element, then value of `data-trk-id` attribute will be used (if available).
     * Otherwise `undefined` will be returned as fallback value (also if there is only empty string).
     * @param {HTMLElement} $element
     * @returns {string|undefined} extracts text/label of an element
     */
    static getElementText($element) {

      let elementText;

      if (this.isElement($element)) {
        const $elementTitle = $element.querySelector('[data-trk-value="title"]') || undefined;
        elementText = this.isElement($elementTitle) ? $elementTitle.textContent.trim() : '';

        if (!elementText) {
          elementText = $element.textContent.trim() || $element.dataset.trkId;
        }
      }

      return elementText;
    }

    /**
     * Returns the "section id" from the markup of a component.
     * @param {HTMLElement} $section - `section` element
     * @returns {string|undefined} Value from `id` attribute of `section`.
     */
    static getComponentSectionId($section = null) {
      /**
        * if the sectionId should be evaluated dynamically (id of elements change
        * on runtime), use this function inside the event listener
        */
      const isDefined = (val) => typeof val === 'string' && !!val;

      if (!this.isElement($section) || !('id' in $section) || !isDefined($section.id)) {
        return undefined;
      }

      return $section.id;
    }
  }

  class ScrollTracker {
    constructor(options) {
      const opts = options || {};
      // trigger all attached callbacks only once the scroll mark was hit
      let triggerOnce = true;
      // trigger all attached callbacks below the current seen scroll mark
      let triggerPrevious = true;

      if (Utils.hasProp(options, 'triggerOnce')) {
        triggerOnce = options.triggerOnce;
      }

      if (Utils.hasProp(options, 'triggerPrevious')) {
        triggerPrevious = options.triggerPrevious;
      }

      this.options = opts;
      this.options.triggerOnce = triggerOnce;
      this.options.triggerPrevious = triggerPrevious;
      this.loaded = false;

      /**
     * Collects all callbacks which should fire on a scroll percentage.
     * @example
     * {
     *  "25": [fn1, fn2],
     *  "50": [fn1, fn2. fn3],
     * }
     */
      this.triggerFunctions = {};

      // make sure 'this' is available inside the event listener
      const boundScrollListener = this.scrollListener.bind(this);
      window.addEventListener('scroll', throttle(boundScrollListener, 100));
    }

    // registers a callback function which should trigger at a percentage
    registerScrollTrigger(percent, fn) {
      if (this.triggerFunctions[percent]) {
        this.triggerFunctions[percent].push(fn);
        return;
      }

      this.triggerFunctions[percent] = [];
      this.triggerFunctions[percent].push(fn);
    }

    static getScrollPercentage() {
      let scrollPercent = window.scrollY / (document.body.offsetHeight - window.innerHeight);
      return Math.round(scrollPercent * 100);
    }

    // executes all callback functions up to the current found scroll mark
    triggerPreviousScrollHeights(currentPercentTrigger) {
      for (let i = 0; i < Object.keys(this.triggerFunctions).length; i += 1) {
        const triggerPercent = Number(Object.keys(this.triggerFunctions)[i]);
        const fns = this.triggerFunctions[triggerPercent];

        if (triggerPercent <= currentPercentTrigger) {
          fns.forEach((fn) => fn());

          if (this.options.triggerOnce) {
            this.triggerFunctions[triggerPercent] = [];
          }
        }
      }
    }

    scrollListener() {
      const staticSelf = this.constructor;
      let scrollPercent = staticSelf.getScrollPercentage();

      for (let i = 0; i < Object.keys(this.triggerFunctions).length; i += 1) {
        const triggerPercent = Number(Object.keys(this.triggerFunctions)[i]);
        const fns = this.triggerFunctions[triggerPercent];

        if (
          !this.loaded
        && this.options.triggerPrevious
        && triggerPercent < scrollPercent
        ) {
          this.triggerPreviousScrollHeights(triggerPercent);
          this.loaded = true;
        }

        // found the next closest scroll mark
        if (triggerPercent <= scrollPercent) {
          if (this.options.triggerPrevious) {
            this.triggerPreviousScrollHeights(triggerPercent);
          } else {
            fns.forEach((fn) => fn());
            if (this.options.triggerOnce) {
              this.triggerFunctions[triggerPercent] = [];
            }
          }
        }
      }
    }
  }
  const scrollTracker = new ScrollTracker({
    triggerOnce: true,
    triggerPrevious: true,
  });

  class DatalayerUtils {
    static get localStorageKey() { return 'products'; }

    /**
   * Return the clientId if GA cookie was set.
   *
   * @static
   * @return {string}
   * @memberof DatalayerUtils
   */
    static getClientId() {
      let gaCookie = Utils.getGaCookie();

      if (!gaCookie) {
        return undefined;
      }

      let clientId = null;

      // remove GAX.X
      clientId = gaCookie.replace(/^GA\d\.\d-?\d?\./, '');

      const regexClientId = /^\d+\.\d+$/;
      if (!regexClientId.test(clientId)) {
        return undefined;
      }

      return clientId;
    }

    /**
   * SessionId is the second part of the by dot seperated digit of the clientId.
   *
   * @static
   * @memberof DatalayerUtils
   */
    static getSessionId() {
      let sessionId = this.getClientId();

      if (!Utils.isUndefined(sessionId)) {
        sessionId = sessionId.split('.')[1];
      }

      return sessionId;
    }

    static getViewDesignSize() {
      return Utils.getCurrentBreakpointName();
    }

    static getDocumentLanguage() {
      return Utils.validateLanguage(document.documentElement.lang);
    }

    static getBrowserLanguage() {
      return Utils.validateLanguage(navigator.language);
    }

    static getViewId() {
    // always starts with a slash
      return '/' + Utils.getURLSlugs().join('/') + window.location.search;
    }

    static getUserAgent() {
      return navigator.userAgent;
    }

    static getHitTime() {
      return +new Date();
    }

    static pushProductsToLocalStorage(dtlProducts) {
    // first product from datalayer
      const dtlFirstProduct = dtlProducts[0];

      // Write initial value if there is none in localStorage
      if (dtlProducts !== 'n/a'
      && Utils.isUndefined(localStorage.getItem(this.localStorageKey))) {
        localStorage.setItem(this.localStorageKey, JSON.stringify(dtlProducts));
        return;
      }

      // Get the existing data
      const localStorageProducts = JSON.parse(localStorage.getItem(this.localStorageKey));

      // Helper function to deduplicate entries
      const productExists = (pageTitle, arr) => {
        for (let i = 0; i < arr.length; i += 1) {
          if (arr[i].name === pageTitle) {
            return true;
          }
        }

        return false;
      };

      // search if the document title already exist
      if (productExists(dtlFirstProduct.name, localStorageProducts)) {
        return;
      }

      // Add new data to localStorage Array
      for (let i = 0; i < dtlProducts.length; i += 1) {
        localStorageProducts.push({
          defaultShippingPrice: dtlProducts[i].defaultShippingPrice,
          id: dtlProducts[i].id,
          name: dtlProducts[i].name,
          price: dtlProducts[i].price,
          quantity: dtlProducts[i].quantity,
          vat: dtlProducts[i].vat,
          _origin: { listItemPosition: dtlProducts[i]._origin.listItemPosition },
        });
      }

      // override localStorage with newly parsed values
      localStorage.setItem(this.localStorageKey, JSON.stringify(localStorageProducts));
    }
  }

  // these properties should be included in every page
  const setBaseDatalayerProperties = (datalayer) => {
    const dl = datalayer;

    // VIEW
    const viewId = dl.view.id + window.location.hash;
    dl.view.id = viewId;
    dl.view.title = document.title;
    dl.view.designSize = DatalayerUtils.getViewDesignSize();
    dl.view.langSystem = DatalayerUtils.getDocumentLanguage();
    dl.view.langClient = DatalayerUtils.getBrowserLanguage();

    // DEVICE
    const device = {
      clientId: DatalayerUtils.getClientId(),
      userAgent: DatalayerUtils.getUserAgent(),
    };
    dl.device = device;

    dl.sessionId = DatalayerUtils.getSessionId();
    dl.hitTime = DatalayerUtils.getHitTime();
  };

  // these properties should only be changed on a Product Landing Page
  const setPlpDatalayerProperties = (datalayer) => {
    const dtl = datalayer;

    dtl.lists.forEach((listItem, i) => {
      listItem.products.forEach((_, j) => {
        dtl.lists[i].products[j].name = document.title;
        dtl.lists[i].products[j].price = Utils.getPageWordCount();
      });
    });
  };

  // these properties should only be changed on a Product Detail Page
  const setPdpDatalayerProperties = (datalayer) => {
    const dtl = datalayer;

    dtl.view.products.forEach((_, i) => {
      dtl.view.products[i].name = document.title;
      dtl.view.products[i].price = Utils.getPageWordCount();
    });
  };

  const pushPageLoadDatalayers = (datalayers) => {
    const dtls = datalayers;

    for (let i = 0; i < Object.keys(dtls).length; i += 1) {
      const event = Object.keys(dtls)[i];
      const datalayer = dtls[event];

      setBaseDatalayerProperties(datalayer);

      switch (event) {
      case 'plp_ecommerce':
        setPlpDatalayerProperties(datalayer);
        break;
      case 'pdp_ecommerce':
        setPdpDatalayerProperties(datalayer);
        break;
      default:
        break;
      }

      Utils.log({ ...datalayer });
      globalDatalayer.push({ ...datalayer });
    }
  };

  const setProductProperties = (datalayer, propName) => {
    let dtl = datalayer;
    dtl[propName].products.forEach((_, i) => {
      dtl[propName].products[i].name = document.title;
      dtl[propName].products[i].price = Utils.getPageWordCount();
    });
  };

  const setupCheckoutEvents = (event, datalayer) => {
    const dtl = datalayer;
    const percentRegex = /[\d]{1,3}$/;
    const scrollPercentage = Number(percentRegex.exec(event)[0]);

    if (event.startsWith('checkout_ecommerce')) {
      setProductProperties(dtl, 'checkout');
    }

    // NOTE don't modify checkout_cartview and checkout_pageview because it's base datalayer

    // push layer when scroll height was reached
    scrollTracker.registerScrollTrigger(scrollPercentage, () => {
      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    });
  };

  const setupAddCartEvent = (event, datalayer) => {
    const dtl = datalayer;
    const percentRegex = /[\d]{1,3}$/;
    const scrollPercentage = Number(percentRegex.exec(event)[0]);

    let products;

    switch (scrollPercentage) {
    case 50:
      setProductProperties(dtl, 'wishlist');
      products = dtl.wishlist.products;
      break;
    case 20:
    case 100:
      setProductProperties(dtl, 'cart');
      products = dtl.cart.products;
      break;
    default:
      break;
    }

    // check if cart object exists and if it has products key
    if (Utils.isUndefined(products)) {
      products = 'n/a';
    }

    // push layer when scroll height was reached,
    // do not push wishlist products to localStorage
    scrollTracker.registerScrollTrigger(scrollPercentage, () => {
      if (Utils.isUndefined(dtl.wishlist)) {
        DatalayerUtils.pushProductsToLocalStorage(products);
      }

      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    });
  };

  const setupPurchaseEvent = (event, datalayer) => {
    const avgTime = 134400; // in ms = 2,24min
    const dtl = datalayer;
    const percentRegex = /[\d]{1,3}$/;
    const scrollPercentage = Number(percentRegex.exec(event)[0]);

    const pageloadTime = +new Date();

    // push layer when scroll height was reached
    scrollTracker.registerScrollTrigger(scrollPercentage, () => {
      const timeSpent = +new Date() - pageloadTime;
      let delta = avgTime - timeSpent;
      if (delta < 0) {
        delta = 0;
      }

      // trigger if timestamp runs out
      setTimeout(() => {
        const products = JSON.parse(
          localStorage.getItem(DatalayerUtils.localStorageKey),
        );

        if (Utils.isUndefined(products)) {
          dtl.checkout.products = [];
          dtl.purchase.products = [];
          dtl.purchase.totalRevenue = 0;
        } else {
          dtl.checkout.products = products;
          dtl.purchase.products = products;
          // sum of words from visited sites
          dtl.purchase.totalRevenue = products.reduce((accumulator, product) => accumulator + product.price, 0);
        }

        Utils.log({ ...dtl });
        globalDatalayer.push({ ...dtl });
        localStorage.removeItem(DatalayerUtils.localStorageKey);
      }, delta);
    });
  };

  /**
   * Setup of "view" event, to track if specified component/element was visible to the user.
   * @param {{
   *   $component: Object,
   *   componentSectionId: string,
   *   eventName: string,
   *   eventInfoCategory: string,
   *   datalayer: Object,
   *   useComponentTitle: boolean,
   *   overrideComponentType: string,
   * }} params
   * @returns -
   */
  const setupComponentEventView = ({
    $component = null,
    componentSectionId = '',
    eventName = '',
    eventInfoCategory = '',
    datalayer = null,
    useComponentTitle = false,
    overrideComponentType = null,
  }) => {
    if (Utils.isUndefined($component) || !eventName || !eventInfoCategory || !datalayer) return;

    const dtl = { ...datalayer };

    const observerOptions = {
      root: null,
      // negative top and bottom padding add up to a 50% detection area in the center
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    let componentTitle = VALUE_NOT_AVAILABLE;
    if (useComponentTitle) {
      componentTitle = Utils.getElementText($component);
    }

    let componentType = VALUE_NOT_AVAILABLE;
    if (overrideComponentType) {
      componentType = overrideComponentType;
    }

    // component seen by the user
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        dtl.event = eventName;
        dtl.hitTime = DatalayerUtils.getHitTime();
        dtl.eventInfo = {
          category: eventInfoCategory,
          action: componentSectionId + ' | View',
          label: componentTitle + ' | ' + componentType,
        };
        Utils.log({ ...dtl });
        globalDatalayer.push({ ...dtl });
      });
    }, observerOptions).observe($component);
  };

  /**
   * Setup of "action" event, to track if specified element was clicked by the user.
   * @param {{
    *   $link: Object,
    *   componentSectionId: string,
    *   eventName: string,
    *   eventInfoCategory: string,
    *   datalayer: Object,
    *   trackLinkUrl: bool
    * }} params
    * @returns -
    */
  const setupComponentLinkEventAction = ({
    $link,
    componentSectionId = '',
    eventName,
    eventInfoCategory,
    datalayer,
    trackLinkUrl = false, // whether to use the link url or internal/external type as event label
  }) => {
    if (Utils.isUndefined($link) || !eventName || !eventInfoCategory || !datalayer) return;

    const dtl = { ...datalayer };

    const linkTitle = Utils.getElementText($link);
    const linkType = Utils.getLinkType($link);
    const linkUrl = $link.href || VALUE_NOT_AVAILABLE;

    $link.addEventListener('click', (evt) => {
      // do not load new page
      if (debugDatalayer) evt.preventDefault();


      dtl.event = eventName;

      dtl.hitTime = DatalayerUtils.getHitTime();

      dtl.eventInfo = {
        category: eventInfoCategory,
        action: componentSectionId + ' | Click',
        label: linkTitle + ' | ' + (trackLinkUrl ? linkUrl : linkType),
      };

      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    });
  };

  /**
   * Setup of multiple "action" events - for specific elements inside a component (defined by selector).
   * @param {{
    *   $component: Object,
    *   componentSectionId: string,
    *   eventName: string,
    *   eventInfoCategory: string,
    *   selectorLinks: string,
    *   datalayer: Object,
    * }} params
    * @returns -
    */
  const setupComponentLinksEventAction = ({
    $component,
    componentSectionId = '',
    eventName,
    eventInfoCategory,
    selectorLinks,
    datalayer,
  }) => {
    if (!$component || !eventName || !eventInfoCategory || !selectorLinks || !datalayer) return;

    // links inside component clicked by the user
    const $links = $component.querySelectorAll(selectorLinks);
    if ($links.length === 0) return;

    const dtl = { ...datalayer };

    $links.forEach($link => {
      setupComponentLinkEventAction({
        $link,
        componentSectionId,
        eventName,
        eventInfoCategory,
        datalayer: dtl,
      });
    });
  };

  /**
   * General setup for default "view" and "action" events.
   * @param {Object} config - all relevant config settings
   * @param {string} config.selectorComponent - selector for components with "view" event
   * @param {string} config.eventNameView - tracking name for "view" event
   * @param {string} config.eventInfoCategory - tracking name for "view" event
   * @param {string} config.selectorLinks - selector for elements with "action" events
   * @param {string} config.eventNameAction - tracking name for "action" event
   * @param {boolean} config.handleLinkEvents - should "action" events be handled?
   * @param {boolean} config.useComponentTitle - should component tile be used in "eventInfo"
   * @param {Object} datalayer - GTM datalayer object
   * @returns -
   */
  const setupTrackingEvents = (config, datalayer) => {

    const {
      selectorComponent,
      eventNameView,
      eventInfoCategory,
      selectorLinks,
      eventNameAction,
      handleLinkEvents,
      useComponentTitle,
    } = config;
    const $components = document.querySelectorAll(selectorComponent);
    if ($components.length === 0) return;

    $components.forEach($component => {
      /* if the sectionId should be evaluated dynamically (id of elements change
       * on runtime), move the assignment into the event listener
       */
      const componentSectionId = Utils.getComponentSectionId($component);

      setupComponentEventView({
        $component,
        componentSectionId,
        eventName: eventNameView,
        eventInfoCategory,
        datalayer,
        useComponentTitle,
      });

      if (handleLinkEvents) {
        setupComponentLinksEventAction({
          $component,
          componentSectionId,
          eventName: eventNameAction,
          eventInfoCategory,
          selectorLinks,
          datalayer,
        });
      }
    });
  };

  /**
   * Handle datalayer Table-of-Contents (TOC) events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupTableOfContentsEvent = (event, datalayer) => {
    const config = {
      selectorComponent: '[data-trk-selector="table-of-contents"]',
      selectorLinks: 'a', // 'a.hero__toc-item-link',
      eventNameView: 'app/toc/view',
      eventNameAction: 'app/toc/action',
      eventInfoCategory: 'Component | Table of contents',
      handleLinkEvents: true,
    };

    setupTrackingEvents(config, datalayer);
  };

  /**
   * Handle datalayer Breadcrumb events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupBreadcrumbEvent = (event, datalayer) => {
    const config = {
      selectorComponent: '[data-trk-selector="breadcrumb"]',
      selectorLinks: 'a.breadcrumb__label',
      eventNameView: 'app/breadcrumbs/view',
      eventNameAction: 'app/breadcrumbs/action',
      eventInfoCategory: 'Component | Breadcrumbs',
      handleLinkEvents: true,
    };

    setupTrackingEvents(config, datalayer);
  };

  /**
     * Handle datalayer CardTeaser events
     * @param {string} event
     * @param {any} datalayer
     * @returns
     */
  const setupCardTeaserEvent = (event, datalayer) => {
    const config = {
      selectorComponent: '[data-trk-selector="card-teaser"]',
      eventNameView: 'app/card/view',
      eventInfoCategory: 'Component | Card',
      useComponentTitle: true,
      handleLinkEvents: false,
    };

    setupTrackingEvents(config, datalayer);
  };

  /**
   * Handle datalayer Card-Link events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupCardEvent = (event, datalayer) => {
    const $teaserCards = document.querySelectorAll('.hover-teaser__teaser');
    if ($teaserCards.length === 0) return;

    const observerOptions = {
      root: null,
      // negative top and bottom padding add up to a 50% detection area in the center
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    $teaserCards.forEach($teaserCard => {
      const dtl = { ...datalayer };

      const $heading = $teaserCard.querySelector('.hover-teaser__teaser__header-heading') || undefined;
      const cardTitle = Utils.getElementText($heading);
      const $section = $teaserCard.closest('section') || {};
      const sectionId = Utils.getComponentSectionId($section);

      // teaserCard seen
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          dtl.event = 'app/card/view';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Card',
            action: sectionId + ' | View',
            label: cardTitle + ' | ' + VALUE_NOT_AVAILABLE,
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }, observerOptions).observe($teaserCard);

      const $cardLink = $teaserCard.querySelector('a.hover-teaser__teaser__header-link') || undefined;
      const linkType = Utils.getLinkType($cardLink);

      // teaserCard hovered
      if (!Utils.isMobile()) {
        $teaserCard.addEventListener('mouseenter', () => {
          dtl.event = 'app/card/action';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Card',
            action: sectionId + ' | Hover',
            label: cardTitle + ' | ' + linkType,
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }

      // cardLink clicked
      if (!Utils.isUndefined($cardLink)) {
        $cardLink.addEventListener('click', (evt) => {
          // do not load new page
          if (debugDatalayer) evt.preventDefault();

          dtl.event = 'app/card/action';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Card',
            action: sectionId + ' | Click',
            label: cardTitle + ' | ' + linkType,
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }
    });
  };

  /**
   * Handle datalayer Basic-card events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupBasicCardEvent = (event, datalayer) => {
    const $basicCardLinks = document.querySelectorAll('[data-trk-selector="basic-card"] a');
    if ($basicCardLinks.length === 0) return;

    const eventInfoCategory = 'Component | Basic Card';

    $basicCardLinks.forEach($basicCardLink => {
      const $section = $basicCardLink.closest('section') || {};
      const componentSectionId = Utils.getComponentSectionId($section);
      const dtl = { ...datalayer };

      // basicCardLink seen
      setupComponentEventView({
        $component: $basicCardLink,
        componentSectionId,
        eventName: 'app/card/view',
        eventInfoCategory,
        datalayer: dtl,
        useComponentTitle: true,
      });

      // basicCardLink clicked
      setupComponentLinkEventAction({
        $link: $basicCardLink,
        componentSectionId,
        eventName: 'app/card/action',
        eventInfoCategory,
        datalayer: dtl,
        trackLinkUrl: true,
      });
      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    });
  };

  /**
   * Handle datalayer CalendarReminder events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupCalendarReminderEvent = (event, datalayer) => {
    const $calendarReminderSections = document.querySelectorAll('[data-trk-selector="calendar-reminder"]');
    const eventInfoCategory = 'Component | CalendarReminder';

    $calendarReminderSections.forEach($calendarReminderSection => {
      const componentSectionId = Utils.getComponentSectionId($calendarReminderSection);
      const dtl = { ...datalayer };

      // CalendarReminder seen
      setupComponentEventView({
        $component: $calendarReminderSection,
        componentSectionId,
        eventName: 'app/calendarreminder/view',
        eventInfoCategory,
        datalayer: dtl,
        useComponentTitle: true,
      });
    });
  };

  /**
   * Handle datalayer Button-Link events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupButtonLinkEvent = (event, datalayer) => {
    // const $buttonLinks = document.querySelectorAll('a.button');
    const $buttonLinks = document.querySelectorAll('[data-trk-selector="button"]');
    if ($buttonLinks.length === 0) return;

    const eventInfoCategory = 'Component | Button';

    $buttonLinks.forEach($buttonLink => {
      const $section = $buttonLink.closest('section') || {};
      const componentSectionId = Utils.getComponentSectionId($section);
      const dtl = { ...datalayer };

      setupComponentEventView({
        $component: $buttonLink,
        componentSectionId,
        eventName: 'app/button/view',
        eventInfoCategory,
        datalayer: dtl,
        useComponentTitle: true,
        overrideComponentType: Utils.getLinkType($buttonLink),
      });

      // buttonLink clicked
      setupComponentLinkEventAction({
        $link: $buttonLink,
        componentSectionId,
        eventName: 'app/button/action',
        eventInfoCategory,
        datalayer: dtl,
      });
    });
  };

  /**
   * Handle datalayer Button events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupButtonEvent = (event, datalayer) => {
    // const $buttons = document.querySelectorAll('button.button');
    const $buttons = document.querySelectorAll('[data-trk-selector="button-element"]');
    if ($buttons.length === 0) return;

    const eventInfoCategory = 'Component | ButtonElement';

    $buttons.forEach($button => {
      const $section = $button.closest('section') || $button.closest('nav') || {};
      const componentSectionId = Utils.getComponentSectionId($section);
      const dtl = { ...datalayer };

      // button seen
      setupComponentEventView({
        $component: $button,
        componentSectionId,
        eventName: 'app/button/view',
        eventInfoCategory,
        datalayer: dtl,
        useComponentTitle: true,
      });

      // button clicked
      setupComponentLinkEventAction({
        $link: $button,
        componentSectionId,
        eventName: 'app/button/action',
        eventInfoCategory,
        datalayer: dtl,
      });
    });
  };

  /**
   * Handle datalayer Header nav events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupHeaderEvent = (event, datalayer) => {
    const $headerNavigation = document.getElementById('header-navi');
    const $headerSocialNavigation = document.getElementById('header-navi-social');
    const $headerButtonMobileMenu = document.querySelector('[data-selector="button-burger"]');

    const clickHandlerLinks = (evt) => {
      if (debugDatalayer) evt.preventDefault();

      const dtl = { ...datalayer };
      const label = evt.currentTarget.textContent.trim() || '';

      // check if link is inside submenu
      const $parentSubmenu = evt.currentTarget.closest('[data-selector="header-submenu-item"]');
      const level = Utils.isElement($parentSubmenu) ? 1 : 0;

      dtl.event = 'app/navigation/action';
      dtl.hitTime = DatalayerUtils.getHitTime();
      dtl.eventInfo = {
        category: 'Navigation | Header | Main',
        action: 'Click',
        label: 'Level ' + level + ' | ' + label,
      };
      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    };

    const clickHandlerSocial = (evt) => {
      // for social navi in footer see "setupFooterEvent" function
      if (debugDatalayer) evt.preventDefault();

      const dtl = { ...datalayer };

      let label = evt.currentTarget.textContent.trim();
      // assuming that the social media icons do not have text content
      if (label === '') {
        label = evt.currentTarget.dataset.trkId || '';
      }

      dtl.event = 'app/navigation/action';
      dtl.hitTime = DatalayerUtils.getHitTime();
      dtl.eventInfo = {
        category: 'Navigation | Header | Social',
        action: 'Click',
        label: 'Level 0 | ' + label,
      };
      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    };

    const clickHandlerButtonMobileMenu = (evt) => {
      if (debugDatalayer) evt.preventDefault();

      const dtl = { ...datalayer };

      const $button = evt.currentTarget;
      const isExpanded = $button.getAttribute('aria-expanded');
      const label = (isExpanded === 'true') ? 'Open' : 'Close';

      const $section = $button.closest('nav') || {};
      const componentSectionId = Utils.getComponentSectionId($section);

      dtl.event = 'app/button/action';
      dtl.hitTime = DatalayerUtils.getHitTime();
      dtl.eventInfo = {
        category: 'Component | ButtonElement',
        action: componentSectionId + ' | Click',
        label: $button.id + ' | ' + label,
      };
      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    };

    if ($headerNavigation) {
      $headerNavigation.querySelectorAll('a:not(.social_link)').forEach(link => {
        link.addEventListener('click', clickHandlerLinks);
      });
    }

    if ($headerSocialNavigation) {
      $headerSocialNavigation.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', clickHandlerSocial);
      });
    }

    if ($headerButtonMobileMenu) {
      $headerButtonMobileMenu.addEventListener('click', clickHandlerButtonMobileMenu);
    }
  };

  /**
   * Handle datalayer Footer nav events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupFooterEvent = (event, datalayer) => {
    // id is generated by wordpress wp_nav_menu() function
    const $footerNavigation = document.getElementById('menu-footer-links');
    const $footerSocialNavigation = document.querySelector('#footer .msd_ent_social_media_networks');

    if (!$footerNavigation || !$footerSocialNavigation) {
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    const intersectionHandler = (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const dtl = { ...datalayer };

        let isFooterNav = Object.is(entry.target, $footerNavigation);

        dtl.event = 'app/navigation/view';
        dtl.hitTime = DatalayerUtils.getHitTime();
        dtl.eventInfo = {
          category: 'Navigation | Footer | ' + (isFooterNav ? 'Main' : 'Social'),
          action: 'View',
          label: 'Level 0 | ' + (isFooterNav ? 'Footer Navigation' : 'Footer Social Navigation'),
        };
        Utils.log({ ...dtl });
        globalDatalayer.push({ ...dtl });
      });
    };

    new IntersectionObserver(intersectionHandler, observerOptions).observe($footerNavigation);
    new IntersectionObserver(intersectionHandler, observerOptions).observe($footerSocialNavigation);

    const clickHandler = (evt) => {
      if (debugDatalayer) evt.preventDefault();

      const dtl = { ...datalayer };

      let isFooterNav = Object.is(evt.target.closest('#' + $footerNavigation.id), $footerNavigation);

      let label = evt.currentTarget.textContent.trim();
      // assuming that the social media icons do not have text content
      if (label === '') {
        label = evt.currentTarget.dataset.trkId || '';
      }

      dtl.event = 'app/navigation/action';
      dtl.hitTime = DatalayerUtils.getHitTime();
      dtl.eventInfo = {
        category: 'Navigation | Footer | ' + (isFooterNav ? 'Main' : 'Social'),
        action: 'Click',
        label: 'Level 0 | ' + label,
      };
      Utils.log({ ...dtl });
      globalDatalayer.push({ ...dtl });
    };

    $footerNavigation.querySelectorAll('li.menu-item a').forEach(link => {
      link.addEventListener('click', clickHandler);
    });
    $footerSocialNavigation.querySelectorAll('a.social_link').forEach(link => {
      link.addEventListener('click', clickHandler);
    });
  };

  /**
   * Handle datalayer Related Blog Posts component events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupRelatedBlogPostsEvent = (event, datalayer) => {
    const $relatedBlogPostsSections = document.querySelectorAll('section.related-blog-posts');

    const isDefined = (val) => typeof val === 'string' && !!val;
    const observerOptions = {
      root: null,
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    if (!$relatedBlogPostsSections) {
      return;
    }

    $relatedBlogPostsSections.forEach(($relatedBlogPostsSection, relatedBlogPostsComponentCount) => {
      const articles = [];
      $relatedBlogPostsSection.querySelectorAll('li.related-blog-posts__item').forEach($relatedBlogPostItem => {
        const article = {
          postId: '',
          title: '',
        };

        article.postId = $relatedBlogPostItem.dataset.postId;
        const $relatedBlogPostLink = $relatedBlogPostItem.querySelector('a.related-blog-posts__item-link');
        article.title = $relatedBlogPostLink.textContent.trim();

        articles.push(article);
      });

      // view
      const intersectionHandler = (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const sectionId = 'id' in $relatedBlogPostsSection && isDefined($relatedBlogPostsSection.id) ? $relatedBlogPostsSection.id : undefined;
          const dtl = { ...datalayer };

          dtl.event = 'app/reco/list/view';
          dtl.scope = 'event';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Related Blog Posts',
            action: sectionId + ' | View',
            label: articles.map((article) => article.title).join(' | '),
          };
          dtl.lists = [];
          dtl.lists[relatedBlogPostsComponentCount] = {
            listSource: 'WPVIP | Related Blog Posts',
            listType: 'Reco | Article Recommendation',
            listLabel: 'Reco | Similiar Items',
            listProperties: {},
            listItemTotals: articles.length,
            listItems: articles.map((article, index) => {
              return {
                name: article.title,
                id: article.postId,
                price: undefined,
                vat: 0,
                quantity: 1,
                categoryLevel1: undefined,
                categoryLevel2: undefined,
                categoryLevel3: undefined,
                categoryLevel4: undefined,
                _origin: {
                  listItemPosition: index + 1,
                },
              };
            }),
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      };

      new IntersectionObserver(intersectionHandler, observerOptions).observe($relatedBlogPostsSection);

      $relatedBlogPostsSection.querySelectorAll('li.related-blog-posts__item').forEach(($relatedBlogPost) => {
        const $section = $relatedBlogPost.closest('section') || {};

        // click
        $relatedBlogPost.addEventListener('click', (evt) => {
          if (debugDatalayer) evt.preventDefault();

          const sectionId = 'id' in $section && isDefined($section.id) ? $section.id : undefined;
          const dtl = { ...datalayer };

          dtl.event = 'app/reco/list/action';
          dtl.scope = 'event';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Related Blog Posts',
            action: sectionId + ' | Click',
            label: 'Article Title | ' + evt.currentTarget.textContent.trim(),
          };
          dtl.listAction = {
            action: 'click',
            lists: [],
          };
          dtl.listAction.lists[relatedBlogPostsComponentCount] = {
            listSource: 'WPVIP | Related Blog Posts',
            listType: 'Article Recommendation',
            listLabel: 'Reco | Similiar Items',
            listProperties: {},
            listItemTotals: articles.length,
            listItems: articles.map((article, index) => {
              return {
                name: article.title,
                id: article.postId,
                price: undefined,
                vat: 0,
                quantity: 1,
                categoryLevel1: undefined,
                categoryLevel2: undefined,
                categoryLevel3: undefined,
                categoryLevel4: undefined,
                _origin: {
                  listItemPosition: index + 1,
                },
              };
            }),
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      });
    });
  };

  /**
   * Handle datalayer CardCarousel component events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupCardCarouselEvent = (event, datalayer) => {
    const eventName = 'app/multi-item-carousel/list';
    const eventInfoCategory = 'Component | Multi-Item Carousel';

    const listSourceValue = 'WPVIP | Multi-Item Carousel';
    const listTypeValue = 'Item Promotion';
    const listLabelValue = 'Promo | Active Items';

    const selectorComponentSection = '[data-module="CardCarousel"]';
    const selectorComponentItemLink = '.card-carousel__slide a';
    const selectorComponentItem = '.card-carousel__slide';

    const $componentSections = document.querySelectorAll(selectorComponentSection);

    const observerOptions = {
      root: null,
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    if (!$componentSections) {
      return;
    }

    const getItemTitle = ($item) => {
      let title = '';

      if ($item) {
        const $itemTitle = $item.querySelector('.card-l__title');
        if ($itemTitle) {
          title = $itemTitle.textContent.trim();
        }
      }

      // return beginning of full content for item if there is no valid title
      if (title === '') {
        title = $item.textContent.trim().substring(0, 20);
      }

      return title;
    };

    $componentSections.forEach(($componentSection, componentCount) => {
      const articles = [];
      const sectionId = Utils.getComponentSectionId($componentSection);

      // prepare list data
      $componentSection.querySelectorAll(selectorComponentItem).forEach($componentItem => {
        const article = {
          postId: '',
          title: '',
        };

        article.postId = $componentItem.dataset.postId;
        article.title = getItemTitle($componentItem);
        articles.push(article);
      });

      // "view" event
      const intersectionHandler = (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const dtl = { ...datalayer };

          dtl.event = `${eventName}/view`;
          dtl.scope = 'event';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: eventInfoCategory,
            action: sectionId + ' | View',
            label: articles.map((article) => article.title).join(' | '),
          };
          dtl.lists = [];
          dtl.lists[componentCount] = {
            listSource: listSourceValue,
            listType: listTypeValue,
            listLabel: listLabelValue,
            listProperties: {},
            listItemTotals: articles.length,
            listItems: articles.map((article, index) => {
              return {
                name: article.title,
                id: article.postId,
                price: undefined,
                vat: 0,
                quantity: 1,
                categoryLevel1: undefined,
                categoryLevel2: undefined,
                categoryLevel3: undefined,
                categoryLevel4: undefined,
                _origin: {
                  listItemPosition: index + 1,
                },
              };
            }),
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      };

      new IntersectionObserver(intersectionHandler, observerOptions).observe($componentSection);

      $componentSection.querySelectorAll(selectorComponentItemLink).forEach(($componentItem, index) => {
        const itemPostId = $componentItem.dataset.postId;
        const itemTitle = getItemTitle($componentItem);

        // "click" event
        $componentItem.addEventListener('click', (evt) => {
          if (debugDatalayer) evt.preventDefault();

          const dtl = { ...datalayer };

          dtl.event = `${eventName}/action`;
          dtl.scope = 'event';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: eventInfoCategory,
            action: sectionId + ' | Click',
            label: itemTitle + ' | ' + evt.currentTarget.href,
          };
          dtl.listAction = {
            action: 'click',
            lists: [],
          };
          dtl.listAction.lists[componentCount] = {
            listSource: listSourceValue,
            listType: listTypeValue,
            listLabel: listLabelValue,
            listProperties: {},
            listItemTotals: 1,
            listItems: [{
              name: itemTitle,
              id: itemPostId,
              price: undefined,
              vat: 0,
              quantity: 1,
              categoryLevel1: undefined,
              categoryLevel2: undefined,
              categoryLevel3: undefined,
              categoryLevel4: undefined,
              _origin: {
                listItemPosition: index + 1,
              },
            }],
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      });
    });
  };

  /**
   * Handle datalayer Tab events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupTabEvent = (event, datalayer) => {
    const $tabMenus = document.querySelectorAll('ul.msd_ent_tab_menu');
    if ($tabMenus.length === 0) return;

    const observerOptions = {
      root: null,
      // negative top and bottom padding add up to a 50% detection area in the center
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    $tabMenus.forEach($tabMenu => {
      const dtl = { ...datalayer };
      const $section = $tabMenu.closest('section') || {};
      const componentSectionId = Utils.getComponentSectionId($section);

      // view
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const $activeTabItem = entry.target.querySelector('.msd_ent_tab_active');
          if (Utils.isUndefined($activeTabItem)) return;

          const tabTitle = $activeTabItem.textContent.trim().replace(/\s+/, ' ');

          dtl.event = 'app/tabs/view';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Tabs',
            action: componentSectionId + ' | View',
            label: tabTitle + ' | Open',
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }, observerOptions).observe($tabMenu);

      const $tabItems = $tabMenu.children;

      // click
      Array.from($tabItems).forEach($tabItem => {
        const tabTitle = $tabItem.textContent.trim().replace(/\s+/, ' ');

        $tabItem.addEventListener('click', (evt) => {
          if (debugDatalayer) evt.preventDefault();

          dtl.event = 'app/tabs/action';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Tabs',
            action: componentSectionId + ' | Click',
            label: tabTitle + ' | Open',
          };

          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      });
    });
  };

  /**
   * Handle datalayer Accordion events
   * AccordionList
   *   AccordionItem
   *     AccordionToggle
   *     AccordionBody
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupAccordionEvent = (event, datalayer) => {
    const $accordions = document.querySelectorAll('[data-selector="AccordionItem"]');
    if ($accordions.length === 0) return;
    const isDefined = (val) => typeof val === 'string' && !!val;

    const observerOptions = {
      root: null,
      // negative top and bottom padding add up to a 50% detection area in the center
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    $accordions.forEach($accordion => {
      const dtl = { ...datalayer };
      const $headline = $accordion.querySelector('[data-selector="AccordionToggle"]');

      let label = !Utils.isUndefined($headline) ? $headline.textContent.trim() : undefined;

      const $section = $accordion.closest('section') || {};
      const getState = () => $accordion.hasAttribute('data-open') ? 'Open' : 'Closed';

      /**
       * if the sectionId should be evaluated dynamically (id of elements change
       * on runtime), move the assignment into the event listener
      */
      const sectionId = 'id' in $section && isDefined($section.id) ? $section.id : undefined;

      // button seen
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          dtl.event = 'app/accordion/view';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Accordion',
            action: sectionId + ' | View',
            label: label + ' | ' + getState(),
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }, observerOptions).observe($accordion);

      // button clicked
      $accordion.addEventListener('click', (evt) => {
        if (debugDatalayer) evt.preventDefault();

        dtl.event = 'app/accordion/action';
        dtl.hitTime = DatalayerUtils.getHitTime();
        dtl.eventInfo = {
          category: 'Component | Accordion',
          action: sectionId + ' | Click',
          label: label + ' | ' + getState(),
        };
        Utils.log({ ...dtl });
        globalDatalayer.push({ ...dtl });
      });
    });
  };

  /**
   * Handle datalayer ContentSwitch events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupContentSwitchEvent = (event, datalayer) => {
    const $contentSwitchTriggers = document.querySelectorAll('[data-selector="ContentSwitchTrigger"]');
    if ($contentSwitchTriggers.length === 0) return;
    const isDefined = (val) => typeof val === 'string' && !!val;

    const observerOptions = {
      root: null,
      // negative top and bottom padding add up to a 50% detection area in the center
      rootMargin: '0% 0% 0% 0%',
      // when any part of the element is passing the intersecting area
      threshold: 0.5,
    };

    $contentSwitchTriggers.forEach($contentSwitchTrigger => {
      const dtl = { ...datalayer };
      let label = $contentSwitchTrigger.textContent.trim();

      const $section = $contentSwitchTrigger.closest('section') || {};
      const getState = () => $contentSwitchTrigger.classList.contains('content-switch__button--active') ? 'Open' : 'Closed';
      const getNextState = () => $contentSwitchTrigger.classList.contains('content-switch__button--active') ? 'Closed' : 'Open';

      /**
         * if the sectionId should be evaluated dynamically (id of elements change
         * on runtime), move the assignment into the event listener
        */
      const sectionId = 'id' in $section && isDefined($section.id) ? $section.id : undefined;

      // button seen
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          dtl.event = 'app/contentswitch/view';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Content Switch',
            action: sectionId + ' | View',
            label: label + ' | ' + getState(),
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }, observerOptions).observe($contentSwitchTrigger);

      // button clicked
      $contentSwitchTrigger.addEventListener('click', (evt) => {
        if (debugDatalayer) evt.preventDefault();

        dtl.event = 'app/contentswitch/action';
        dtl.hitTime = DatalayerUtils.getHitTime();
        dtl.eventInfo = {
          category: 'Component | Content Switch',
          action: sectionId + ' | Click',
          label: label + ' | ' + getNextState(),
        };
        Utils.log({ ...dtl });
        globalDatalayer.push({ ...dtl });
      });
    });
  };

  /**
   * Handle datalayer Quiz events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupQuizEvent = (event, datalayer) => {
    const $quizSections = document.querySelectorAll('[data-trk-selector="Quiz"]');

    $quizSections.forEach($quizSection => {
      const $quizButtons = $quizSection.querySelectorAll('.quiz-button');

      if ($quizButtons.length === 0) return;

      const eventInfoCategory = 'Component | Quiz';

      const $section = $quizSection.closest('section') || {};
      const componentSectionId = Utils.getComponentSectionId($section);
      const dtl = { ...datalayer };

      // quiz seen
      setupComponentEventView({
        $component: $quizSection,
        componentSectionId,
        eventName: 'app/quiz/view',
        eventInfoCategory,
        datalayer: dtl,
        useComponentTitle: true,
      });

      $quizButtons.forEach($quizButton => {
        // button clicked
        $quizButton.addEventListener('click', (evt) => {
          if (debugDatalayer) evt.preventDefault();
          if (evt.currentTarget.hasAttribute('disabled')) return;

          const $quizButtonLabel = $quizButton.innerText;

          dtl.event = 'app/quiz/action';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Quiz',
            action: 'Quiz | Click',
            label: $quizButtonLabel + ' | undefined',
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      });
    });
  };

  /**
   * Track Poll Completed Event
   * @param {any} datalayer
   * @param {string} eventInfoCategory
   * @param {string} componentSectionId
   * @returns
   */
  const trackPollCompleteEvent = (datalayer, eventInfoCategory, componentSectionId) => {
    const dtl = { ...datalayer };

    dtl.event = 'app/poll/action';
    dtl.hitTime = DatalayerUtils.getHitTime();
    dtl.eventInfo = {
      category: eventInfoCategory,
      action: `${componentSectionId} | Completed`,
      label: 'N/A | N/A',
    };
    Utils.log({ ...dtl });
    globalDatalayer.push({ ...dtl });
  };

  /**
   * Handle datalayer Poll events
   * @param {string} event
   * @param {any} datalayer
   * @returns
   */
  const setupPollEvent = (event, datalayer) => {
    const $pollSections = document.querySelectorAll(PollSelectors.component);

    $pollSections.forEach($poll => {
      const eventInfoCategory = 'Component | Poll';
      const componentSectionId = Utils.getComponentSectionId($poll);
      const dtl = { ...datalayer };

      // Poll Seen
      setupComponentEventView({
        $component: $poll,
        componentSectionId,
        eventName: 'app/poll/view',
        eventInfoCategory,
        datalayer: dtl,
        useComponentTitle: true,
      });

      const $answers = $poll.querySelectorAll(`.${PollSelectors.question.answers.button}`);
      if ($answers.length === 0) return;

      const question = $poll.querySelector(`.${PollSelectors.question.title}`).innerText.trim();
      $answers.forEach($answer => {
        // First level Button Click
        if (!$answer.hasAttribute('data-shows-follow-up-question')) return;

        $answer.addEventListener('click', () => {
          const answer = $answer.innerText.trim();

          dtl.event = 'app/poll/action';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: eventInfoCategory,
            action: `${componentSectionId} | Click`,
            label: `${question} | ${answer}`,
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });

          if ($answer.dataset.showsFollowUpQuestion !== '1') {
            trackPollCompleteEvent(dtl, eventInfoCategory, componentSectionId);
          }
        });
      });

      const $feedbackButton = $poll.querySelector(`.${PollSelectors.feedbackButton}`);
      $feedbackButton.addEventListener('click', () => {
        const $followUpQuestion = $poll.querySelector(`.${PollSelectors.question.followUp}`);

        // Follow-up question Event
        if ($followUpQuestion) {
          const $followUpAnswers = $followUpQuestion.querySelectorAll(`.${PollSelectors.question.answers.buttonSelected}`);

          const selectedAnswers = Array.from($followUpAnswers).map((b) => b.innerText.trim()).join(' + ');
          const followUpQuestion = $followUpQuestion.querySelector(`.${PollSelectors.question.title}`).innerText.trim();

          dtl.event = 'app/poll/action';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: eventInfoCategory,
            action: `${componentSectionId} | Click`,
            label: `${followUpQuestion} | ${selectedAnswers}`,
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        }

        // Track complete event
        trackPollCompleteEvent(dtl, eventInfoCategory, componentSectionId);
      });
    });
  };

  /**
   * Handle reimbursement events
   * @param {string} event
   * @param {any} datalayer
   */
  const setupReimbursementEvent = (event, datalayer) => {
    const $reimbursementSections = document.querySelectorAll('[data-module="Reimbursement"]');

    if ($reimbursementSections.length === 0) return;

    $reimbursementSections.forEach($reimbursementSection => {
      const $section = $reimbursementSection.closest('section') || {};
      const componentSectionId = Utils.getComponentSectionId($section);
      const dtl = { ...datalayer };
      const observerOptions = {
        root: null,
        // negative top and bottom padding add up to a 50% detection area in the center
        rootMargin: '0% 0% 0% 0%',
        // when any part of the element is passing the intersecting area
        threshold: 0.5,
      };

      const $elementInput = $reimbursementSection.querySelector('[data-selector="reimbursement__autocomplete__input"]');

      let componentTitle = VALUE_NOT_AVAILABLE;
      let componentType = VALUE_NOT_AVAILABLE;

      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          if ($elementInput instanceof HTMLInputElement ) {
            componentTitle = $elementInput.value.trim() || VALUE_NOT_AVAILABLE;
          }

          dtl.event = 'app/reimbursement/view';
          dtl.hitTime = DatalayerUtils.getHitTime();
          dtl.eventInfo = {
            category: 'Component | Reimbursement',
            action: componentSectionId + ' | View',
            label: componentTitle + ' | ' + componentType,
          };
          Utils.log({ ...dtl });
          globalDatalayer.push({ ...dtl });
        });
      }, observerOptions).observe($reimbursementSection);

      const clickHandler = (evt) => {
        if (debugDatalayer) evt.preventDefault();

        if(evt.detail){
          componentTitle = evt.detail.value || VALUE_NOT_AVAILABLE;
        }

        dtl.event = 'app/reimbursement/action';
        dtl.hitTime = DatalayerUtils.getHitTime();
        dtl.eventInfo = {
          category: 'Component | Reimbursement',
          action: componentSectionId + ' | Click',
          label: componentTitle + ' | ' + 'Search',
        };
        Utils.log({ ...dtl });
        globalDatalayer.push({ ...dtl });
      };

      if ($elementInput instanceof HTMLInputElement ) {
        $elementInput.addEventListener(
          'ReimbursementSelectionEvent',
          clickHandler,
        );
      }
    });
  }

  const pushEventDatalayers = (datalayers) => {
    const dtls = datalayers;

    for (let i = 0; i < Object.keys(dtls).length; i += 1) {
      const event = Object.keys(dtls)[i];
      const datalayer = dtls[event];

      setBaseDatalayerProperties(datalayer);

      if (event.startsWith('cart_ecommerce')) {
        setupAddCartEvent(event, datalayer);
      } else if (event.startsWith('checkout_')) {
        setupCheckoutEvents(event, datalayer);
      } else if (event.startsWith('purchase_ecommerce_')) {
        setupPurchaseEvent(event, datalayer);
      } else if (event === 'button_link') {
        setupButtonLinkEvent(event, datalayer);
      } else if (event === 'button') {
        setupButtonEvent(event, datalayer);
      } else if (event === 'breadcrumb') {
        setupBreadcrumbEvent(event, datalayer);
      } else if (event === 'table_of_contents') {
        setupTableOfContentsEvent(event, datalayer);
      } else if (event === 'card') {
        setupCardEvent(event, datalayer);
      } else if (event === 'card_teaser') {
        setupCardTeaserEvent(event, datalayer);
      } else if (event === 'header') {
        setupHeaderEvent(event, datalayer);
      } else if (event === 'footer') {
        setupFooterEvent(event, datalayer);
      } else if (event === 'related_blog_posts') {
        setupRelatedBlogPostsEvent(event, datalayer);
      } else if (event === 'tab') {
        setupTabEvent(event, datalayer);
      } else if (event === 'accordion') {
        setupAccordionEvent(event, datalayer);
      } else if (event === 'basic-card') {
        setupBasicCardEvent(event, datalayer);
      } else if (event === 'card-carousel') {
        setupCardCarouselEvent(event, datalayer);
      } else if (event === 'content_switch') {
        setupContentSwitchEvent(event, datalayer);
      } else if (event === 'quiz') {
        setupQuizEvent(event, datalayer);
      } else if (event === 'poll') {
        setupPollEvent(event, datalayer);
      } else if (event === 'calendar-reminder') {
        setupCalendarReminderEvent(event, datalayer);
      } else if (event === 'reimbursement') {
        setupReimbursementEvent(event, datalayer);
      }
    }
  };

  const main = () => {
  // early exit, could not parse from backend
    if (Utils.isUndefined(window.msd_datalayers)) {
      return;
    }

    Utils.replaceNullWithUndefined(window.msd_datalayers);

    for (let i = 0; i < Object.keys(window.msd_datalayers).length; i += 1) {
      const type = Object.keys(window.msd_datalayers)[i];
      const datalayers = window.msd_datalayers[type];

      switch (type) {
      case 'pageload':
        pushPageLoadDatalayers(datalayers);
        break;
      case 'event':
        pushEventDatalayers(datalayers);
        break;
      default:
        break;
      }
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    main();
  });
})(
  // we need the custom object due to naming conventions of GTM (lg vs l)
  {
    xs: BREAKPOINT_NAME_XS,
    sm: BREAKPOINT_NAME_S,
    md: BREAKPOINT_NAME_M,
    lg: BREAKPOINT_NAME_L,
    xl: BREAKPOINT_NAME_XL,
    xxl: BREAKPOINT_NAME_XXL,
  },
  window[DATALAYER_NAME] || [],
);
